import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { DndContext, DragOverlay, PointerSensor, TouchSensor, useSensor, useSensors, useDraggable, useDroppable, closestCenter } from '@dnd-kit/core';
import { MagnifyingGlass, Trash, Sparkle, CaretDoubleLeft, CaretDoubleRight, Plus, DotsSixVertical, ArrowsLeftRight } from '@phosphor-icons/react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const SLOTS = [
  { id: 'breakfast', label: 'Breakfast', emoji: '🍳' },
  { id: 'lunch', label: 'Lunch', emoji: '🍛' },
  { id: 'dinner', label: 'Dinner', emoji: '🌙' },
  { id: 'snacks', label: 'Snacks', emoji: '☕' },
];
const CATS = [
  { id: 'all', label: 'All' },
  { id: 'breakfast', label: '🍳 Breakfast' },
  { id: 'lunch', label: '🍛 Lunch' },
  { id: 'dinner', label: '🌙 Dinner' },
  { id: 'snacks', label: '☕ Snacks' },
  { id: 'liked', label: '❤️ Liked' },
  { id: 'suggested', label: '✨ Suggested' },
  { id: 'library', label: '📚 Library' },
];
const valToItems = (v) => {
  const s = typeof v === 'string' ? v : (v && typeof v === 'object' ? (v.default || '') : '');
  return s.split(/[,/]/).map(x => x.trim()).filter(Boolean);
};
const fmtDay = (d) => new Date(d + 'T00:00').toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });

function DishCard({ name, img, small, dragging }) {
  return (
    <div className={`flex items-center gap-2 rounded-xl border select-none ${small ? 'px-2 py-1.5' : 'px-2.5 py-2'} ${dragging ? 'shadow-2xl ring-2 ring-[#FF6B35] scale-105' : ''}`}
      style={{ background: '#1d2026', borderColor: 'rgba(255,255,255,0.1)', touchAction: 'none' }}>
      {img ? (
        <img src={`${API_BASE}${img}`} alt="" loading="lazy" decoding="async"
          className={`${small ? 'w-8 h-8' : 'w-10 h-10'} rounded-lg object-cover flex-shrink-0`} />
      ) : (
        <div className={`${small ? 'w-8 h-8' : 'w-10 h-10'} rounded-lg flex-shrink-0 flex items-center justify-center text-sm bg-white/5`}>🍽️</div>
      )}
      <span className={`${small ? 'text-[11px]' : 'text-xs'} font-semibold text-neutral-200 leading-tight`}>{name}</span>
    </div>
  );
}

function BoardChip({ id, name, img }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id });
  return (
    <div ref={setNodeRef} {...listeners} {...attributes} data-testid={`board-chip`}
      className={`cursor-grab active:cursor-grabbing ${isDragging ? 'opacity-30' : ''}`}>
      <DishCard name={name} img={img} small />
    </div>
  );
}

function PaletteCard({ item }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: `pal|${item.name}` });
  return (
    <div ref={setNodeRef} {...listeners} {...attributes} data-testid="palette-card"
      className={`cursor-grab active:cursor-grabbing ${isDragging ? 'opacity-30' : ''}`}>
      <DishCard name={item.name} img={item.image_url} small />
    </div>
  );
}

function SlotLane({ planId, slot, items, imgOf, onSuggest }) {
  const { setNodeRef, isOver } = useDroppable({ id: `slot|${planId}|${slot.id}` });
  return (
    <div ref={setNodeRef} data-testid={`lane-${planId}-${slot.id}`}
      className={`rounded-xl p-2 min-h-[58px] transition-colors border ${isOver ? 'border-[#FF6B35] bg-[#FF6B35]/10' : 'border-white/5 bg-white/[0.02]'}`}>
      <div className="flex items-center justify-between mb-1.5">
        <p className="text-[9px] uppercase tracking-wider font-bold text-neutral-500">{slot.emoji} {slot.label}</p>
        <button onClick={() => onSuggest(slot.id)} title="AI suggest for this slot" data-testid={`suggest-${planId}-${slot.id}`}
          className="text-purple-300/70 hover:text-purple-200"><Sparkle size={11} weight="fill" /></button>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {items.map((name, i) => (
          <BoardChip key={`${name}-${i}`} id={`chip|${planId}|${slot.id}|${i}|${name}`} name={name} img={imgOf(name)} />
        ))}
        {items.length === 0 && <span className="text-[10px] text-neutral-600 italic px-1">drop dishes here</span>}
      </div>
    </div>
  );
}

function TrashZone({ active }) {
  const { setNodeRef, isOver } = useDroppable({ id: 'trash' });
  if (!active) return null;
  return (
    <div ref={setNodeRef} data-testid="board-trash"
      className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-8 py-4 rounded-2xl border-2 border-dashed flex items-center gap-2 text-sm font-bold transition-all ${isOver ? 'bg-red-500/30 border-red-400 text-red-200 scale-110' : 'bg-red-500/10 border-red-500/40 text-red-300'}`}>
      <Trash size={18} weight="fill" /> Drop here to remove
    </div>
  );
}

function DayHandle({ planId, date }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: `day|${planId}|${date}` });
  return (
    <button ref={setNodeRef} {...listeners} {...attributes} data-testid={`day-handle-${planId}`}
      title="Drag onto another day to swap the two full menus"
      className={`cursor-grab active:cursor-grabbing px-1 py-1 rounded-lg text-neutral-500 hover:text-white hover:bg-white/10 ${isDragging ? 'opacity-40' : ''}`}
      style={{ touchAction: 'none' }}>
      <DotsSixVertical size={15} weight="bold" />
    </button>
  );
}

function DayDrop({ planId, active, children }) {
  const { setNodeRef, isOver } = useDroppable({ id: `daydrop|${planId}` });
  return (
    <div ref={setNodeRef} data-testid={`day-drop-${planId}`}
      className={`rounded-2xl transition-all ${active && isOver ? 'ring-2 ring-[#FF6B35] scale-[1.01]' : ''}`}>
      {children}
    </div>
  );
}

const dishKeyOf = (n) => String(n).toLowerCase().trim().replace(/[^a-z0-9\s]/g, '').replace(/\s+/g, '_');

export default function MenuBoardView({ api, plans, onUpdate }) {
  const [board, setBoard] = useState({});
  const [palette, setPalette] = useState([]);
  const [search, setSearch] = useState('');
  const [cat, setCat] = useState('all');
  const [selectedDays, setSelectedDays] = useState(new Set());
  const [activeDrag, setActiveDrag] = useState(null);
  const [suggesting, setSuggesting] = useState(false);
  const [customName, setCustomName] = useState('');
  const [showPalette, setShowPalette] = useState(true);
  const [newDayDate, setNewDayDate] = useState('');

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 10 } }),
  );

  useEffect(() => {
    const b = {};
    (plans || []).forEach(p => {
      b[p.plan_id] = Object.fromEntries(SLOTS.map(s => [s.id, valToItems(p[s.id])]));
    });
    setBoard(b);
  }, [plans]);

  useEffect(() => {
    let cancelled = false;
    let tries = 0;
    const fetchPalette = async () => {
      try {
        const r = await api.get('/meals/dish-palette');
        if (cancelled) return;
        setPalette(r.data.items || []);
        // Photos paint server-side in the background — refresh a few times until done
        if ((r.data.missing_images || 0) > 0 && tries < 12) {
          tries += 1;
          setTimeout(fetchPalette, 12000);
        }
      } catch (_) {}
    };
    fetchPalette();
    return () => { cancelled = true; };
  }, [api]);

  const imgMap = useMemo(() => {
    const m = {};
    palette.forEach(it => { if (it.image_url) m[it.dish_key] = it.image_url; });
    return m;
  }, [palette]);
  const imgOf = useCallback((name) => imgMap[dishKeyOf(name)], [imgMap]);

  const saveSlot = async (planId, slotId, items) => {
    try {
      await api.patch(`/meals/plans/${planId}/slot`, { slot: slotId, items });
    } catch (e) { toast.error('Could not save — reloading'); onUpdate(); }
  };

  const onDragEnd = ({ active, over }) => {
    setActiveDrag(null);
    if (!over) return;
    const a = String(active.id).split('|');
    const next = JSON.parse(JSON.stringify(board));
    const overId = String(over.id);

    if (a[0] === 'day') {
      const srcPid = a[1];
      let dstPid = null;
      if (overId.startsWith('daydrop|') || overId.startsWith('slot|') || overId.startsWith('chip|')) {
        dstPid = overId.split('|')[1];
      }
      if (!dstPid || dstPid === srcPid || !next[dstPid]) return;
      const tmp = next[srcPid];
      next[srcPid] = next[dstPid];
      next[dstPid] = tmp;
      setBoard(next);
      SLOTS.forEach(s => {
        saveSlot(srcPid, s.id, next[srcPid][s.id]);
        saveSlot(dstPid, s.id, next[dstPid][s.id]);
      });
      toast.success('Full menus swapped between the two days 🔄');
      return;
    }

    let target = null; // {planId, slotId, index|null}
    if (overId.startsWith('slot|')) {
      const [, pid, sid] = overId.split('|');
      target = { pid, sid, idx: null };
    } else if (overId.startsWith('chip|')) {
      const [, pid, sid, idx] = overId.split('|');
      target = { pid, sid, idx: parseInt(idx, 10) };
    }

    if (a[0] === 'pal') {
      const name = a[1];
      if (!target) return;
      const list = next[target.pid][target.sid];
      if (list.some(x => x.toLowerCase() === name.toLowerCase())) { toast.info('Already on that meal'); return; }
      list.splice(target.idx ?? list.length, 0, name);
      setBoard(next);
      saveSlot(target.pid, target.sid, list);
      toast.success(`Added ${name}`);
      return;
    }

    if (a[0] === 'chip') {
      const [, srcPid, srcSid, srcIdxS, name] = a;
      const srcIdx = parseInt(srcIdxS, 10);
      const srcList = next[srcPid][srcSid];
      if (overId === 'trash') {
        srcList.splice(srcIdx, 1);
        setBoard(next);
        saveSlot(srcPid, srcSid, srcList);
        toast.success(`Removed ${name}`);
        return;
      }
      if (!target) return;
      if (target.pid === srcPid && target.sid === srcSid) {
        const to = target.idx ?? srcList.length - 1;
        if (to === srcIdx) return;
        srcList.splice(srcIdx, 1);
        srcList.splice(to > srcIdx ? to : to, 0, name);
        setBoard(next);
        saveSlot(srcPid, srcSid, srcList);
      } else {
        const dstList = next[target.pid][target.sid];
        if (dstList.some(x => x.toLowerCase() === name.toLowerCase())) { toast.info('Already on that meal'); return; }
        srcList.splice(srcIdx, 1);
        dstList.splice(target.idx ?? dstList.length, 0, name);
        setBoard(next);
        saveSlot(srcPid, srcSid, srcList);
        saveSlot(target.pid, target.sid, dstList);
        toast.success(`Moved ${name}`);
      }
    }
  };

  const addDay = async (dateStr) => {
    const existing = new Set((plans || []).map(p => p.date));
    let date = dateStr;
    if (!date) {
      const base = plans?.length ? plans[plans.length - 1].date : new Date().toISOString().slice(0, 10);
      const d = new Date(base + 'T00:00');
      do { d.setDate(d.getDate() + 1); date = d.toISOString().slice(0, 10); } while (existing.has(date));
    } else if (existing.has(date)) { toast.info('That day is already on the board'); return; }
    try {
      await api.post('/meals/plans', { date, breakfast: '', lunch: '', dinner: '', snacks: '' });
      toast.success(`${fmtDay(date)} added — drag dishes onto it! 🎨`);
      onUpdate();
    } catch (e) { toast.error('Could not add day'); }
  };

  useEffect(() => {
    const h = () => addDay('');
    window.addEventListener('board-add-day', h);
    return () => window.removeEventListener('board-add-day', h);
  });

  const shiftSelected = async (days) => {
    const ids = [...selectedDays];
    if (!ids.length) return;
    try {
      for (const pid of ids) {
        await api.patch(`/meals/plans/${pid}`, { shift_days: days });
      }
      toast.success(`Moved ${ids.length} day(s) by ${days > 0 ? '+' : ''}${days} day(s)`);
      setSelectedDays(new Set());
      onUpdate();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not move dates'); onUpdate(); }
  };

  const suggest = async (slotId, date) => {
    if (suggesting) return;
    setSuggesting(true);
    const excl = palette.filter(p => p.category === 'suggested').map(p => p.name);
    try {
      const r = await api.post('/meals/suggest-items', { slot: slotId, date, exclude: excl });
      const fresh = (r.data.suggestions || []).map(name => ({ name, dish_key: dishKeyOf(name), category: 'suggested', image_url: imgMap[dishKeyOf(name)] }));
      setPalette(prev => [...fresh.filter(f => !prev.some(p => p.dish_key === f.dish_key)), ...prev]);
      setCat('suggested');
      setShowPalette(true);
      toast.success(`✨ ${fresh.length} fresh ${slotId} ideas added to the palette`);
    } catch (e) { toast.error('Could not get suggestions'); }
    setSuggesting(false);
  };

  const addCustom = () => {
    const name = customName.trim();
    if (!name) return;
    const key = dishKeyOf(name);
    setPalette(prev => prev.some(p => p.dish_key === key) ? prev : [{ name, dish_key: key, category: 'suggested', image_url: imgMap[key] }, ...prev]);
    setCat('suggested');
    setCustomName('');
    toast.success(`"${name}" added to palette — drag it onto a meal`);
  };

  const filtered = palette.filter(it =>
    (cat === 'all' || it.category === cat) &&
    (!search || it.name.toLowerCase().includes(search.toLowerCase())));

  const dragStr = activeDrag ? String(activeDrag) : '';
  const dragName = dragStr.startsWith('pal|') ? dragStr.split('|')[1] : dragStr.startsWith('chip|') ? dragStr.split('|')[4] : null;
  const dragDay = dragStr.startsWith('day|') ? dragStr.split('|')[2] : null;

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter}
      onDragStart={({ active }) => setActiveDrag(active.id)} onDragEnd={onDragEnd} onDragCancel={() => setActiveDrag(null)}>
      <div className="flex gap-3 items-start" data-testid="menu-board">
        {/* Board */}
        <div className="flex-1 min-w-0 space-y-3">
          {selectedDays.size > 0 && (
            <div className="sticky top-0 z-20 rounded-xl px-3 py-2 flex items-center gap-2 flex-wrap" style={{ background: '#241d2e', border: '1px solid rgba(168,85,247,0.4)' }} data-testid="board-bulk-bar">
              <span className="text-xs font-bold text-purple-200">{selectedDays.size} day(s) selected — move:</span>
              {[-7, -1, 1, 7].map(d => (
                <button key={d} onClick={() => shiftSelected(d)} data-testid={`board-shift-${d}`}
                  className="px-2.5 py-1 rounded-full text-[11px] font-bold bg-purple-500/20 text-purple-200 border border-purple-500/40 hover:bg-purple-500/30">
                  {d > 0 ? `+${d}d` : `${d}d`}
                </button>
              ))}
              <button onClick={() => setSelectedDays(new Set())} className="ml-auto text-[11px] text-neutral-400 hover:text-white underline">Clear</button>
            </div>
          )}
          {(plans || []).map(p => (
            <DayDrop key={p.plan_id} planId={p.plan_id} active={!!activeDrag && String(activeDrag).startsWith('day|') && String(activeDrag).split('|')[1] !== p.plan_id}>
            <div className="rounded-2xl p-3" data-testid={`board-day-${p.plan_id}`}
              style={{ background: 'rgba(255,255,255,0.03)', border: selectedDays.has(p.plan_id) ? '1px solid rgba(168,85,247,0.5)' : '1px solid rgba(255,255,255,0.07)' }}>
              <div className="flex items-center gap-1 mb-2">
                <DayHandle planId={p.plan_id} date={p.date} />
                <label className="flex items-center gap-2 cursor-pointer w-fit">
                  <input type="checkbox" checked={selectedDays.has(p.plan_id)} data-testid={`board-day-check-${p.plan_id}`}
                    onChange={e => setSelectedDays(prev => { const n = new Set(prev); e.target.checked ? n.add(p.plan_id) : n.delete(p.plan_id); return n; })}
                    className="accent-purple-500" />
                  <span className="text-sm font-bold text-white">{fmtDay(p.date)}</span>
                </label>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-2">
                {SLOTS.map(s => (
                  <SlotLane key={s.id} planId={p.plan_id} slot={s} items={board[p.plan_id]?.[s.id] || []}
                    imgOf={imgOf} onSuggest={(sid) => suggest(sid, p.date)} />
                ))}
              </div>
            </div>
            </DayDrop>
          ))}
          {(plans || []).length === 0 && (
            <p className="text-center text-neutral-500 text-sm py-10">No upcoming days — add one below or generate with AI Chef.</p>
          )}
          <div className="rounded-2xl p-4 border-2 border-dashed border-white/15 hover:border-[#FF6B35]/50 transition-colors flex items-center justify-between gap-3 flex-wrap" data-testid="board-add-day">
            <div>
              <p className="text-sm font-bold text-white">➕ Add a new day</p>
              <p className="text-[11px] text-neutral-500">Creates an empty day — then just drag dishes onto it</p>
            </div>
            <div className="flex items-center gap-2">
              <input type="date" data-testid="board-add-day-date"
                onChange={e => setNewDayDate(e.target.value)} value={newDayDate}
                className="bg-white/5 border border-white/10 rounded-xl px-2.5 py-2 text-xs text-white" />
              <button onClick={() => addDay(newDayDate)} data-testid="board-add-day-btn"
                className="px-4 py-2 rounded-xl bg-[#FF6B35] text-white text-xs font-bold flex items-center gap-1.5">
                <Plus size={13} weight="bold" /> Add Day
              </button>
            </div>
          </div>
        </div>

        {/* Palette */}
        <div className={`${showPalette ? 'w-[290px]' : 'w-10'} flex-shrink-0 transition-all sticky top-2 max-h-[80vh] flex flex-col rounded-2xl`}
          style={{ background: '#16181d', border: '1px solid rgba(255,255,255,0.1)' }} data-testid="board-palette">
          <button onClick={() => setShowPalette(v => !v)} data-testid="palette-toggle"
            className="p-2 text-neutral-400 hover:text-white self-start">
            {showPalette ? <CaretDoubleRight size={14} /> : <CaretDoubleLeft size={14} />}
          </button>
          {showPalette && (
            <div className="px-3 pb-3 flex flex-col min-h-0">
              <div className="relative mb-2">
                <MagnifyingGlass size={13} className="absolute left-2.5 top-2.5 text-neutral-500" />
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search dishes…" data-testid="palette-search"
                  className="w-full bg-white/5 border border-white/10 rounded-xl pl-8 pr-2 py-2 text-xs text-white placeholder:text-neutral-600 focus:outline-none focus:border-[#FF6B35]/60" />
              </div>
              <div className="flex flex-wrap gap-1 mb-2">
                {CATS.map(c => (
                  <button key={c.id} onClick={() => setCat(c.id)} data-testid={`palette-cat-${c.id}`}
                    className={`px-2 py-1 rounded-full text-[10px] font-bold border ${cat === c.id ? 'bg-[#FF6B35] text-white border-[#FF6B35]' : 'bg-white/5 text-neutral-400 border-white/10 hover:text-white'}`}>
                    {c.label}
                  </button>
                ))}
              </div>
              <div className="flex gap-1.5 mb-2">
                <input value={customName} onChange={e => setCustomName(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && addCustom()} placeholder="Add your own dish…" data-testid="palette-custom-input"
                  className="flex-1 bg-white/5 border border-white/10 rounded-xl px-2.5 py-1.5 text-[11px] text-white placeholder:text-neutral-600 focus:outline-none" />
                <button onClick={addCustom} data-testid="palette-custom-add" className="px-2 rounded-xl bg-white/5 border border-white/10 text-neutral-300 hover:text-white"><Plus size={12} /></button>
              </div>
              <p className="text-[9px] text-neutral-600 mb-2">Drag any card onto a meal · drag chips between days · drop on the red zone to remove · ⠿ next to a date swaps two full days · ✨ on a slot asks AI for fresh ideas</p>
              <div className="overflow-y-auto min-h-0 space-y-1.5 pr-1" data-testid="palette-list">
                {filtered.slice(0, 120).map(it => <PaletteCard key={it.dish_key} item={it} />)}
                {filtered.length === 0 && <p className="text-[11px] text-neutral-600 italic text-center py-6">No dishes match</p>}
              </div>
            </div>
          )}
        </div>
      </div>

      <TrashZone active={!!activeDrag && String(activeDrag).startsWith('chip|')} />
      <DragOverlay dropAnimation={null}>
        {dragName ? <DishCard name={dragName} img={imgOf(dragName)} dragging /> : dragDay ? (
          <div className="px-4 py-2.5 rounded-2xl border-2 border-[#FF6B35] bg-[#1d2026] shadow-2xl flex items-center gap-2 text-sm font-bold text-white">
            <ArrowsLeftRight size={16} className="text-[#FF6B35]" /> {fmtDay(dragDay)} — drop on another day to swap menus
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
